import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faPlus, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

import api from "../../../services/api";
import { authenticationService } from "../../../services/auth.service";
import toastOptions from "../../../utils/toastOptions";
import { TablePage } from "../../../containers";
import DialogConfirm from "../../../components/DialogConfirm";

interface EcclesiasticalFunctionDTO {
  id: number;
  description: string;
}

export default function EcclesiasticalFunctionList() {
  const navigate = useNavigate();
  const [isReadOnly] = useState<boolean>(!authenticationService.canChange());
  const [refresh, setRefresh] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [pageNo, setPageNo] = useState<number>(1);
  const [isDialogDelete, setIsDialogDelete] = useState<boolean>(false);
  const [dialogDeleteText, setDialogDeleteText] = useState<string[]>([]);
  const [deletingId, setDeletingId] = useState<number>(0);
  const functionSelected = useRef<number>(0);

  const columns = [
    {
      name: "Descrição",
      sortable: true,
      grow: 3,
      cell: (row: EcclesiasticalFunctionDTO) => <span>{row.description}</span>
    },
    {
      name: "Ações",
      button: true,
      sortable: false,
      cell: (row: EcclesiasticalFunctionDTO) => (
        <div>
          {deletingId === row.id ? (
            <div uk-spinner="true; ratio:0.5"></div>
          ) : (
            <>
              <Link
                to={`/sistema/funcoes-eclesiasticas/${row.id}`}
                uk-tooltip={`title: ${isReadOnly ? "Visualizar" : "Editar"}`}
                className="uk-margin-right"
              >
                <FontAwesomeIcon icon={isReadOnly ? faSearch : faPencil} />
              </Link>
              {!isReadOnly && (
                <button
                  type="button"
                  uk-tooltip="title: Excluir"
                  className="uk-button uk-button-link uk-padding-remove uk-margin-right"
                  onClick={() => {
                    handleRemove(row);
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              )}
            </>
          )}
        </div>
      )
    }
  ];

  const fetchEcclesiasticalFunctions = async (page: number, limit: number) => {
    try {
      const response = await api.get("ecclesiastical-functions", {
        params: { page, limit }
      });

      setTotal(response.data.total);
      setPageNo(page);

      return response;
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });

      return {
        data: { data: [], total: 0 }
      };
    }
  };

  const handleRemove = (row: EcclesiasticalFunctionDTO) => {
    setDialogDeleteText([`Deseja realmente excluir ${row.description}?`]);
    functionSelected.current = row.id;
    setIsDialogDelete(true);
  };

  const dialogClose = () => {
    setIsDialogDelete(false);
    setDialogDeleteText([]);
  };

  const dialogConfirm = async () => {
    setIsDialogDelete(false);
    try {
      setDeletingId(functionSelected.current);
      await api.delete(`ecclesiastical-functions/${functionSelected.current}`);
      toast.success("Função eclesiástica removida com sucesso!", toastOptions);
      setRefresh(!refresh);
      dialogClose();
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setDeletingId(0);
    }
  };

  return (
    <div id="page-ecclesiastical-function-list">
      <TablePage
        title={"Funções Eclesiásticas"}
        columns={columns}
        fetch={fetchEcclesiasticalFunctions}
        refresh={refresh}
        showSubHeader
        totalRecords={total}
        labelTotalRecords={`${total} ${total === 1 ? "Função" : "Funções"}`}
        pageNo={pageNo}
        messageEmpty={<span>Nenhuma <b>função eclesiástica</b> encontrada!</span>}
        showAddButton={!isReadOnly}
        handleAdd={() => {
          navigate("/sistema/funcoes-eclesiasticas/novo");
        }}
      />
      <DialogConfirm
        open={isDialogDelete}
        text={dialogDeleteText}
        afterClose={dialogClose}
        handleConfirmation={dialogConfirm}
      />
    </div>
  );
}
